import { loadDeck } from '../../../../maps/basemap';
import { Finding } from '../rules/suburb-map-checks';
import { SubRegionAction } from '../rules/suburb-subregion-edits';
import { openBasemap } from './map-basemap';
import { MapCamera } from './map-camera';
import { EditMode } from './map-editing';
import { CheckResults, MapEditor } from './map-editor';
import {
  REGION_BOUNDS_STEP,
  anchorOf,
  bboxOf,
  boundsAround,
  codesInPath,
  mainlandBounds,
} from './map-geometry';
import { LassoTool, ModifierKey } from './map-lasso';
import { SuburbLayerView, suburbLayers } from './map-layers';
import { PlaceMarkers } from './map-markers';
import { wirePainting } from './map-paint';
import { featuresIn, hoverInfo } from './map-queries';
import { lassoColour } from './map-style';
import {
  EngineHooks,
  Gesture,
  LngLat,
  MapMode,
  MapPlace,
  ScreenPoint,
  SubRegionPick,
} from './map-types';
import { SuburbMapModel } from './suburb-map.model';

export * from './map-types';
export { anchorOf, bboxOf, displayName } from './map-geometry';
export type { SearchHit } from './map-queries';

/**
 * The suburb map engine (#256; plan §6.1): Google Maps for the basemap, deck.gl drawing the ABS suburb polygons on the
 * GPU, and the standalone mapper's tools (click, shift-drag paint, lasso, polygon, grow, checks). Framework-free on
 * purpose: the workspace owns the panels, the API and undo; the engine owns the map and the local copy of the
 * assignments, and reports every user action as a list of edits that it has already applied locally.
 *
 * Why deck.gl and not google.maps.Data: the data layer makes a DOM/SVG overlay per feature and bogs down well under a
 * thousand polygons; NSW alone has 4,542. deck.gl renders them all and hit-tests through its picking buffer. deck.gl is
 * a vendored UMD bundle (assets/vendor/deck.gl-9.0.36), loaded when the map opens: its npm typings don't compile on the
 * client's TypeScript 4.3 without skipLibCheck (plan §6.1), and the map stays out of the main bundle.
 *
 * The facade: SuburbMapModel holds the state and its rules, and this class adds the map on screen, handing the work
 * to its modules (README.md in suburbs/ lists them).
 */
export class SuburbMapEngine extends SuburbMapModel {
  private deck: any;
  private overlay: any;
  private readonly editor: MapEditor;
  private readonly camera = new MapCamera(() => this.render());
  private readonly lassoTool: LassoTool;
  private readonly modifier = new ModifierKey();
  private readonly markers = new PlaceMarkers(() => this.render());
  private hoverCode: string | null = null;
  private teardown: Array<() => void> = [];
  private layers: any[] = [];

  constructor(
    private mapEl: HTMLElement,
    private canvas: HTMLCanvasElement,
    hooks: EngineHooks
  ) {
    super(hooks);
    this.editor = new MapEditor(this, hooks);
    this.lassoTool = new LassoTool(canvas, {
      armed: () => this.lassoArmed(),
      freehand: () => this.tool === 'lasso' || (this.modifier.held && this.tool !== 'polygon'),
      polygon: () => this.tool === 'polygon',
      colour: () =>
        lassoColour(
          this.mode,
          this.subRegionAction,
          this.subRegionById(this.activeSubRegion)?.rgb,
          this.regionById(this.activeRegion)?.rgb
        ),
      apply: (path) =>
        this.editor.lasso(
          codesInPath(path, this.visibleFeatures(), (lng, lat) => this.project(lng, lat))
        ),
      status: (message) => this.hooks.onStatus(message),
    });
  }

  /** The selected suburb's places, drawn over it (map-markers.ts). */
  get places(): MapPlace[] {
    return this.markers.places;
  }

  get selectedPlace(): number | null {
    return this.markers.selected;
  }

  /** Where the pin of a place being added by hand is (null: not in pin mode). */
  get pin(): LngLat | null {
    return this.markers.pin;
  }

  get basemap(): 'google' | 'maplibre' | null {
    return this.camera.map?.kind ?? null;
  }

  /** Pixels on the right covered by the workspace's side panel: fitting a suburb or region keeps clear of them. */
  get rightInset(): number {
    return this.camera.rightInset;
  }
  set rightInset(px: number) {
    this.camera.rightInset = px;
  }

  /** True while a lasso or polygon is being drawn (Esc cancels it, rather than closing anything else). */
  get drawing(): boolean {
    return this.lassoTool.drawing;
  }

  // ------------------------------------------------------------------ setup

  async init(): Promise<void> {
    this.deck = await loadDeck();
    const basemap = await openBasemap(this.mapEl, this.deck, {
      zoom: (z) => this.camera.onZoom(z),
    });
    this.camera.map = basemap.map;
    this.overlay = basemap.overlay;
    this.teardown.push(
      wirePainting(this.mapEl, {
        canPaint: () => this.mode !== 'view',
        pick: (x, y) =>
          this.overlay?.pickObject({ x, y, radius: 1 })?.object?.properties?.code ?? null,
        edit: (code, before) => this.editor.editCodes([code], 'sweep', before),
        setDraggable: (on) => this.camera.setDraggable(on),
        redraw: () => this.render(),
        done: (edits, before) => {
          this.markChanged();
          this.hooks.onAction(edits, 'paint', before);
        },
      }),
      this.lassoTool.wire(),
      this.modifier.wire(() => this.syncToolCursor())
    );
    const ro = new ResizeObserver(() => this.remeasure());
    ro.observe(this.mapEl);
    this.teardown.push(() => ro.disconnect());
    this.syncToolCursor();
  }

  destroy(): void {
    for (const t of this.teardown.splice(0)) t();
    this.camera.map?.destroy();
    this.overlay?.finalize?.();
    this.overlay = null;
    this.camera.map = null;
  }

  /** A state was loaded: draw it, arm the tools for it and fit the mainland part of it. */
  protected showLoaded(): void {
    this.render();
    this.syncToolCursor();
    requestAnimationFrame(() => this.camera.fit(mainlandBounds(this.features)));
    this.markChanged();
  }

  // ------------------------------------------------------------------ editing (map-editor.ts)

  editOne(code: string, mode: EditMode, label: string): number {
    return this.editor.editOne(code, mode, label);
  }

  editMembership(
    code: string,
    subRegionId: string,
    action: SubRegionAction,
    label: string
  ): number {
    return this.editor.editMembership(code, subRegionId, action, label);
  }

  grow(): Promise<void> {
    return this.editor.grow();
  }

  checks(): Promise<CheckResults> {
    return this.editor.checks();
  }

  fillEnclaves(findings: Finding[]): number {
    return this.editor.fillEnclaves(findings);
  }

  /** One action over these suburbs (the specs set up with it). */
  private act(
    codes: string[],
    gesture: Gesture,
    label: string,
    mode?: MapMode,
    sub?: SubRegionPick
  ): number {
    return this.editor.act(codes, gesture, label, mode, sub);
  }

  // ------------------------------------------------------------------ the view (map-camera.ts)

  flyToFeature(code: string): void {
    const f = this.features.find((x) => x.properties.code === code);
    this.camera.fit(f ? bboxOf([f]) : null);
    this.hoverCode = code;
    this.render();
  }

  flyToRegion(regionId: string): void {
    const name = this.regionById(regionId)?.name ?? regionId;
    this.flyToMembers(featuresIn(this.features, this.areas, { regionId }), name);
  }

  flyToSubRegion(id: string): void {
    const name = this.subRegionById(id)?.name ?? id;
    this.flyToMembers(featuresIn(this.features, this.areas, { subRegionId: id }), name);
  }

  /** Load the suburb's state if needed, then centre on it. */
  async flyToCode(code: string): Promise<void> {
    const node = (await this.graph()).nodes[code];
    if (!node) return;
    const [, st, lng, lat] = node;
    if (this.overview || this.stateCode !== st.toLowerCase())
      await this.loadState(st.toLowerCase());
    this.camera.fit(boundsAround(lng, lat));
    this.hoverCode = code;
    this.render();
  }

  setView(lng: number, lat: number, zoom: number): void {
    this.camera.setView(lng, lat, zoom);
  }

  viewCentre(): [number, number] | null {
    return this.camera.centre();
  }

  /** Where a suburb's interior point is on screen, relative to the map element (the specs click there). */
  screenPoint(code: string): ScreenPoint | null {
    const f = this.features.find((x) => x.properties.code === code);
    if (!f) return null;
    const [lng, lat] = anchorOf(f);
    return this.project(lng, lat);
  }

  /** Where a place is on screen (the e2e specs click its marker there). */
  placePoint(id: number): ScreenPoint | null {
    const p = this.markers.find(id);
    return p ? this.project(p.lng, p.lat) : null;
  }

  layerIds(): string[] {
    return this.layers.map((l: any) => l.id);
  }

  private project(lng: number, lat: number): ScreenPoint | null {
    return this.camera.project(lng, lat);
  }

  /** Fit a region's or sub-region's loaded suburbs, or say it has none here. */
  private flyToMembers(feats: any[], name: string): void {
    if (!feats.length) return this.hooks.onStatus(`${name} has no suburbs in ${this.where()} yet.`);
    this.camera.fit(bboxOf(feats, REGION_BOUNDS_STEP));
  }

  private where(): string {
    return this.overview ? 'any state' : (this.stateCode ?? '').toUpperCase();
  }

  private remeasure(): void {
    this.camera.resize();
    this.lassoTool.fit();
  }

  // ------------------------------------------------------------------ places and the pin (map-markers.ts)

  setPlaces(places: MapPlace[], onPick: (id: number) => void): void {
    this.markers.set(places, onPick);
  }

  selectPlace(id: number | null): void {
    this.markers.select(id);
  }

  setPin(pin: LngLat | null, onPick: ((lng: number, lat: number) => void) | null): void {
    this.markers.setPin(pin, onPick);
  }

  // ------------------------------------------------------------------ drawing (map-layers.ts, map-place-layers.ts)

  render(): void {
    if (!this.overlay) return;
    const layers = [
      ...suburbLayers(this.deck, this.layerView(), {
        hover: (info) => this.onHover(info),
        click: (info) => this.onSuburbClick(info),
      }),
      ...this.markers.layers(this.deck, this.camera.zoom),
    ];
    this.layers = layers.filter(Boolean);
    this.overlay.setProps({ layers: this.layers });
  }

  private layerView(): SuburbLayerView {
    return {
      stateCode: this.stateCode,
      features: this.visibleFeatures(),
      areas: this.areas,
      regions: this.regions,
      subRegions: this.subRegions,
      pages: this.pages,
      selectedCode: this.selectedCode,
      hoverCode: this.hoverCode,
      revision: this.revision,
      editingSubRegion: this.editingSubRegion(),
      showSubRegions: this.showSubRegions,
      hiddenSubRegions: this.hiddenSubRegions,
      showLabels: this.showLabels,
      zoom: this.camera.zoom,
    };
  }

  /** The sub-region drawn as being edited: Sub-region mode's active one, or Assign mode's pick. */
  private editingSubRegion(): string | null {
    if (this.mode === 'subregion') return this.activeSubRegion;
    if (this.mode === 'assign') return this.assignSubRegionRef?.id ?? null;
    return null;
  }

  /** View mode: a click selects (the workspace opens the suburb's panel). Edit modes: a click edits, nothing slides over. */
  private onSuburbClick(info: any): void {
    if (this.markers.pinClick(info)) return;
    const code = info?.object?.properties?.code;
    if (!code) return;
    if (this.mode !== 'view') {
      this.act([code], 'click', this.mode);
      return;
    }
    this.selectedCode = code;
    this.render();
    this.hooks.onSelect(code);
  }

  private onHover(info: any): void {
    const code: string | null = info?.object?.properties?.code ?? null;
    if (code !== this.hoverCode) {
      this.hoverCode = code;
      this.render();
    }
    this.hooks.onHover(
      code
        ? hoverInfo(info, this.areas, this.pages, (id) => this.subRegionById(id)?.name ?? id)
        : null
    );
  }

  // ------------------------------------------------------------------ tools

  /** The canvas takes pointer events only while a lasso is armed; otherwise it would swallow every map click. */
  private lassoArmed(): boolean {
    return (
      this.editable &&
      this.mode !== 'view' &&
      (this.tool === 'lasso' || this.tool === 'polygon' || this.modifier.held)
    );
  }

  syncToolCursor(): void {
    const armed = this.lassoArmed();
    this.canvas.style.pointerEvents = armed ? 'auto' : 'none';
    this.canvas.style.cursor = armed ? 'crosshair' : 'default';
    if (!armed) this.lassoTool.clear();
    // Pan stays on with the pointer tool; it's locked only while a lasso is armed.
    this.camera.setDraggable(!armed);
  }
}
